import { useCallback, useEffect, useRef, type ReactElement } from 'react';
import { Button } from '@/components/atoms/button';

const HEADING_ID = 'approve-candidate-modal-heading';

const COPY = {
  heading: 'Approve as new piece',
  bodyPrefix: 'Publish ',
  bodySuffix: ' as a reviewed piece? This can’t be undone.',
  confirm: 'Approve',
  confirming: 'Approving…',
  cancel: 'Cancel',
} as const;

export type ApproveCandidateModalProps = {
  candidateTitle: string;
  isApproving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function ApproveCandidateModal({
  candidateTitle,
  isApproving,
  onConfirm,
  onCancel,
}: ApproveCandidateModalProps): ReactElement {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  const handleEscape = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    },
    [onCancel],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={HEADING_ID}
        className="relative w-full max-w-md space-y-5 rounded-t-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-6 shadow-xl sm:rounded-[var(--r-md)]"
      >
        <div>
          <h2
            id={HEADING_ID}
            className="font-display text-xl font-semibold tracking-tight text-[var(--ink)]"
          >
            {COPY.heading}
          </h2>
          <p className="mt-2 text-sm text-[var(--ink-3)]">
            {COPY.bodyPrefix}
            <span className="font-medium text-[var(--ink)]">{candidateTitle}</span>
            {COPY.bodySuffix}
          </p>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button ref={cancelRef} variant="secondary" size="sm" onClick={onCancel}>
            {COPY.cancel}
          </Button>
          <Button variant="primary" size="sm" onClick={onConfirm} disabled={isApproving}>
            {isApproving ? COPY.confirming : COPY.confirm}
          </Button>
        </div>
      </div>
    </div>
  );
}
